// ============================================================================
// brand.js — Single Source of Truth for verticalstack
// ----------------------------------------------------------------------------
// All brand tokens live here. Every component, page, and document that needs
// a brand reference imports from this file. Never hardcode brand strings
// elsewhere in the codebase.
//
// On rebrand: update this file first. Everything that imports from it inherits
// the change automatically. See verticalstack_Rebrand_Runbook.docx for items
// that cannot be auto-inherited (static HTML, external services, legal).
// ----------------------------------------------------------------------------
// v1.1  ·  2026-04-28
// Changes from v1.0:
//   + appUrl, loginUrl, signupUrl, dashboardUrl tokens added (S-RoutingFix)
//   + Marketing pages now link to /app for login (architectural separation)
// ============================================================================

export const brand = {
  // --------------------------------------------------------------------------
  // NAME — always lowercase, even at sentence start (Brand Guidelines §1)
  // --------------------------------------------------------------------------
  name: "verticalstack",
  legalName: "verticalstack, Inc.",
  tagline: "stack more, manage less",
  domain: "verticalstack.ai",
  url: "https://verticalstack.ai",

  // --------------------------------------------------------------------------
  // APP ROUTES — React SPA mounted at /app (S-RoutingFix Apr 28 2026)
  // --------------------------------------------------------------------------
  appUrl:       "/app",
  loginUrl:     "/app",
  signupUrl:    "/app",
  dashboardUrl: "/app/dashboard",

  // --------------------------------------------------------------------------
  // CONTACT
  // --------------------------------------------------------------------------
  supportEmail: "hello@verticalstack.ai",
  contactEmail: "hello@verticalstack.ai",
  pricingEmail: "hello@verticalstack.ai",
  legalEmail: "hello@verticalstack.ai",
  privacyEmail: "hello@verticalstack.ai",

  // --------------------------------------------------------------------------
  // BUSINESS ADDRESS
  // --------------------------------------------------------------------------
  address: {
    line1: "315 Tuscarawas St W",
    city: "Canton",
    state: "OH",
    postalCode: "44702",
    country: "USA",
    formatted: "315 Tuscarawas St W, Canton, OH 44702",
  },

  // --------------------------------------------------------------------------
  // COLOR PALETTE (Brand Guidelines §4)
  // --------------------------------------------------------------------------
  colors: {
    cyanBlue:     "#00C2D4",  // "vertical" wordmark on light, links, badges
    darkNavy:     "#1A3266",  // "stack" wordmark on light, nav, header, footer
    vibrantCyan:  "#00E5FF",  // "vertical" wordmark on dark, CTAs on dark
    slate:        "#4D6070",  // muted variant, secondary text, icons
    deepDark:     "#0D1B2A",  // dark logo background, dark hero sections
    lightBlueGray:"#E5EEF2",  // muted logo bg, page bg, cards
    offWhite:     "#F4F8FB",  // page background (default)
    tagGray:      "#8A9BAA",  // tagline, captions, secondary body
    white:        "#FFFFFF",
    black:        "#000000",
  },

  // --------------------------------------------------------------------------
  // LOGO PATHS — only use approved variant on its approved background
  // (Brand Guidelines §5)
  // --------------------------------------------------------------------------
  logos: {
    // v2 Text Accent — PRIMARY for marketing & UI on white/off-white
    logoLight: "/assets/verticalstack_logo_b_text_v2_text_accent.png",
    // v1 Dark Inverse — for Deep Dark (#0D1B2A) backgrounds
    logoDark: "/assets/verticalstack_logo_b_text_v1_dark_inverse.png",
    // v3 Vibrant Cyan — for pure black (OLED, signage, video)
    logoVibrant: "/assets/verticalstack_logo_b_text_v3_vibrant_cyan.png",
    // v4 Slate Muted — for legal, letterhead, watermarks
    logoMuted: "/assets/verticalstack_logo_b_text_v4_slate_muted.png",
  },

  // --------------------------------------------------------------------------
  // TYPOGRAPHY (Brand Guidelines §9)
  // --------------------------------------------------------------------------
  fonts: {
    primary:   "'Inter', 'Nunito Sans', system-ui, -apple-system, sans-serif",
    headings:  "'Inter', 'Nunito Sans', system-ui, -apple-system, sans-serif",
    body:      "'Inter', system-ui, -apple-system, sans-serif",
    mono:      "'JetBrains Mono', 'Menlo', 'Consolas', monospace",
  },

  weights: {
    wordmark: 700,  // Bold
    headings: 600,  // SemiBold
    body:     400,  // Regular
  },

  // --------------------------------------------------------------------------
  // META — for <head> tags
  // --------------------------------------------------------------------------
  meta: {
    title: "verticalstack — stack more, manage less",
    description: "verticalstack is the property ecosystem network for owners, property managers, subcontractors, and short-term-rental hosts. Parcel-centric infrastructure where owners have choices.",
    themeColor: "#1A3266",
    ogImage: "/assets/verticalstack_logo_b_text_v2_text_accent.png",
  },

  // --------------------------------------------------------------------------
  // FOOTER
  // --------------------------------------------------------------------------
  footer: {
    copyright: `© ${new Date().getFullYear()} verticalstack, Inc. All rights reserved.`,
    legal: "verticalstack, Inc.  ·  315 Tuscarawas St W, Canton, OH 44702",
  },
};

export default brand;
